// NewCaseWizard — 3-step flow: pick type → fill subject → review.
// On confirm:
//   - property case: createListing() — backend's createCaseForListing
//     auto-creates the linked case (ON CONFLICT DO NOTHING). We then
//     look it up via listCases({type:'property'}) by listing_id since
//     the listing-create response doesn't carry the case id.
//   - client case: createCase() directly; listing_id isn't required.
// Then invalidate the cached cases list so the sidebar + dashboard
// pick it up, and navigate to the new case page.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { invalidateCases } from "@/lib/agentCases";
import {
  createCase,
  createListing,
  listCases,
  type CaseInput,
  type CaseStage,
  type CaseUrgency,
} from "@/lib/proagentApi";
import {
  AIHint,
  Btn,
  Chip,
  Field,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  Select,
  TextArea,
  TextInput,
} from "./ModalShell";

type CaseTypeChoice = "property" | "client" | null;

type WizardForm = {
  // property side
  address?: string;
  price?: string;
  size?: string;
  beds?: string;
  seller?: string;
  engagement?: string;
  // client side
  clientName?: string;
  phone?: string;
  brief?: string;
  budget?: string;
  stage?: string;
  timeline?: string;
};

export function NewCaseWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [type, setType] = useState<CaseTypeChoice>(null);
  const [form, setForm] = useState<WizardForm>({});
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState<string>("");

  useEffect(() => {
    if (open) {
      setStep(0);
      setType(null);
      setForm({});
      setSubmitting(false);
      setErrMsg("");
    }
  }, [open]);

  const canNext =
    (step === 0 && type) ||
    (step === 1 && type === "property" && !!form.address?.trim()) ||
    (step === 1 && type === "client" && !!form.clientName?.trim()) ||
    step === 2;

  async function handleCreate() {
    if (!type) return;
    setSubmitting(true);
    setErrMsg("");

    try {
      if (type === "property") {
        // Property cases require a listing on the wire (the backend
        // store enforces this via the case_type → listing_id check).
        // Earlier this called createCase({case_type:'property'}) with
        // no listing_id and silently 400'd — the modal's "Open case"
        // button would just re-enable with no UX feedback.
        //
        // The right path: createListing(...) using the address /
        // price / size / beds the wizard already collects. The
        // backend's createCaseForListing auto-creates the linked
        // case under the listing on insert; we then look it up by
        // listing_id since the listing-create response doesn't
        // carry the case id.
        const beds = parseInt((form.beds ?? "").trim(), 10);
        const sqft = parseFloat((form.size ?? "").trim());
        const askingPrice = parseMoney(form.price);
        const listing = await createListing({
          project_name: guessProjectName(form.address ?? ""),
          street: form.address ?? "",
          property_type: "condo",
          beds: Number.isFinite(beds) ? beds : null,
          area_sqft: Number.isFinite(sqft) ? sqft : null,
          asking_price: askingPrice ?? null,
          list_status: "active",
          mandate_type: "co_broke",
          owner_email: (form.seller ?? "").trim(),
        });

        // Find the auto-created property case for this listing.
        // ListCases is bound to the same agent so we can't see other
        // agents' rows — the only candidate that matches is ours.
        const cases = await listCases({ type: "property" });
        const myCase = cases.find((c) => c.listing_id === listing.id);

        invalidateCases();
        onClose();
        if (myCase) {
          navigate(`/pro/cases/${myCase.id}`);
        } else {
          // Fallback: listing was created but we couldn't find the
          // case (maybe a race condition with the cache). Land on
          // the cases portfolio so the user sees the new entry.
          navigate("/pro/cases");
        }
        return;
      }

      // Client case path — listing_id is intentionally absent.
      const input: CaseInput = {
        case_type: type,
        stage: form.stage ? mapStage(form.stage) : "New",
        // The buyer's timeline is the real urgency signal — persist it as
        // urgency (the field the buyer report + dashboard actually read)
        // instead of hardcoding "medium" and dropping the agent's choice.
        urgency: mapTimelineToUrgency(form.timeline),
        client_name: form.clientName ?? "",
        client_phone: form.phone ?? "",
        short_name: form.clientName ?? "",
        subject: form.brief ?? "",
        budget_sgd: parseMoney(form.budget),
        next_action: "Initial requirements call",
        next_action_due: "this week",
      };
      const created = await createCase(input);
      invalidateCases();
      onClose();
      navigate(`/pro/cases/${created.id}`);
    } catch (err) {
      // Surface the failure in the modal so the user knows the
      // button click registered + something went wrong server-side.
      console.error("create case failed", err);
      setErrMsg(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  return (
    <ModalShell open={open} onClose={onClose} width={680} label="New Case · wizard">
      <ModalHeader
        eyebrow={t("agent.flows.newCase.step", { current: step + 1, total: 3, defaultValue: `Step ${step + 1} of 3` })}
        title={
          step === 0
            ? t("agent.flows.newCase.step1Title", { defaultValue: "What kind of case?" })
            : step === 1
              ? type === "property"
                ? t("agent.flows.newCase.step2PropertyTitle", { defaultValue: "Tell us about the property" })
                : t("agent.flows.newCase.step2ClientTitle", { defaultValue: "Tell us about the client" })
              : t("agent.flows.newCase.step3Title", { defaultValue: "Review & open case" })
        }
        hint={
          step === 0
            ? t("agent.flows.newCase.step1Hint", {
                defaultValue: "A case is one engagement — selling a property, or helping a client buy.",
              })
            : null
        }
        onClose={onClose}
      />

      <ModalBody>
        {step === 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <CaseTypeCard
              active={type === "property"}
              onClick={() => setType("property")}
              title={t("agent.flows.newCase.propertyTitle", { defaultValue: "Selling a property" })}
              subtitle={t("agent.flows.newCase.propertySubtitle", {
                defaultValue: "Property-centric · buyer leads pipeline",
              })}
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M3 11l9-7 9 7v9a1 1 0 01-1 1h-5v-7H9v7H4a1 1 0 01-1-1v-9z" />
                </svg>
              }
              examples={[
                t("agent.flows.newCase.propertyEx1", { defaultValue: "One specific listing" }),
                t("agent.flows.newCase.propertyEx2", { defaultValue: "You market it, run viewings" }),
                t("agent.flows.newCase.propertyEx3", { defaultValue: "Goal: best buyer, best price" }),
              ]}
            />
            <CaseTypeCard
              active={type === "client"}
              onClick={() => setType("client")}
              title={t("agent.flows.newCase.clientTitle", { defaultValue: "Helping a client buy" })}
              subtitle={t("agent.flows.newCase.clientSubtitle", {
                defaultValue: "Client-centric · property shortlist",
              })}
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21v-1a8 8 0 0116 0v1" />
                </svg>
              }
              examples={[
                t("agent.flows.newCase.clientEx1", { defaultValue: "One buyer engagement" }),
                t("agent.flows.newCase.clientEx2", { defaultValue: "You shortlist & advise" }),
                t("agent.flows.newCase.clientEx3", { defaultValue: "Goal: right home, fair terms" }),
              ]}
            />
          </div>
        )}

        {step === 1 && type === "property" && (
          <>
            <Field
              label={t("agent.flows.newCase.address", { defaultValue: "Property address" })}
              hint={t("agent.flows.newCase.addressHint", {
                defaultValue: "Paste from a listing URL — we'll auto-fill the rest.",
              })}
            >
              <TextInput
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                placeholder="e.g. V on Shenton #31-02, 5 Shenton Way"
              />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label={t("agent.flows.newCase.askingPrice", { defaultValue: "Asking price" })}>
                <TextInput
                  mono
                  value={form.price}
                  onChange={(v) => setForm((f) => ({ ...f, price: v }))}
                  placeholder="$1,450,000"
                />
              </Field>
              <Field label={t("agent.flows.newCase.size", { defaultValue: "Size (sqft)" })}>
                <TextInput
                  mono
                  value={form.size}
                  onChange={(v) => setForm((f) => ({ ...f, size: v }))}
                  placeholder="743"
                />
              </Field>
              <Field label={t("agent.flows.newCase.beds", { defaultValue: "Bedrooms" })}>
                <TextInput
                  value={form.beds}
                  onChange={(v) => setForm((f) => ({ ...f, beds: v }))}
                  placeholder="1+Study"
                />
              </Field>
            </div>
            <Field label={t("agent.flows.newCase.seller", { defaultValue: "Seller" })}>
              <TextInput
                value={form.seller}
                onChange={(v) => setForm((f) => ({ ...f, seller: v }))}
                placeholder="Seller name (e.g. Mr Wong)"
              />
            </Field>
            <Field label={t("agent.flows.newCase.engagement", { defaultValue: "Engagement type" })}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Exclusive", "Co-broke", "Open listing"].map((opt) => (
                  <Chip
                    key={opt}
                    active={form.engagement === opt}
                    onClick={() => setForm((f) => ({ ...f, engagement: opt }))}
                  >
                    {opt}
                  </Chip>
                ))}
              </div>
            </Field>
            <AIHint>
              {t("agent.flows.newCase.propertyAI", {
                defaultValue:
                  "When you create the case, ProProp will pull comparable transactions, draft listing copy, and suggest a viewing calendar.",
              })}
            </AIHint>
          </>
        )}

        {step === 1 && type === "client" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 12 }}>
              <Field label={t("agent.flows.newCase.clientName", { defaultValue: "Client name" })}>
                <TextInput
                  value={form.clientName}
                  onChange={(v) => setForm((f) => ({ ...f, clientName: v }))}
                  placeholder="e.g. Alex Tan"
                />
              </Field>
              <Field label={t("agent.flows.newCase.phone", { defaultValue: "Phone" })}>
                <TextInput
                  mono
                  value={form.phone}
                  onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                  placeholder="+65 ····"
                />
              </Field>
            </div>
            <Field label={t("agent.flows.newCase.brief", { defaultValue: "What are they looking for?" })}>
              <TextArea
                value={form.brief}
                onChange={(v) => setForm((f) => ({ ...f, brief: v }))}
                rows={3}
                placeholder="e.g. 1+Study near Tanjong Pagar MRT, ~$1.4M, will trade size for location, prefer freehold."
              />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label={t("agent.flows.newCase.budget", { defaultValue: "Budget" })}>
                <TextInput
                  mono
                  value={form.budget}
                  onChange={(v) => setForm((f) => ({ ...f, budget: v }))}
                  placeholder="$1.4M"
                />
              </Field>
              <Field label={t("agent.flows.newCase.stage", { defaultValue: "Stage" })}>
                <Select
                  value={form.stage}
                  onChange={(v) => setForm((f) => ({ ...f, stage: v }))}
                  options={[
                    { value: "Briefing", label: t("agent.flows.newCase.stageOpt.briefing", { defaultValue: "Briefing" }) },
                    { value: "Shortlisting", label: t("agent.flows.newCase.stageOpt.shortlisting", { defaultValue: "Shortlisting" }) },
                    { value: "Viewings", label: t("agent.flows.newCase.stageOpt.viewings", { defaultValue: "Viewings" }) },
                    { value: "Offer", label: t("agent.flows.newCase.stageOpt.offer", { defaultValue: "Offer" }) },
                  ]}
                />
              </Field>
              <Field label={t("agent.flows.newCase.timeline", { defaultValue: "Timeline" })}>
                <Select
                  value={form.timeline}
                  onChange={(v) => setForm((f) => ({ ...f, timeline: v }))}
                  options={[
                    { value: "This month", label: t("agent.flows.newCase.timelineOpt.thisMonth", { defaultValue: "This month" }) },
                    { value: "1-3 months", label: t("agent.flows.newCase.timelineOpt.oneToThree", { defaultValue: "1–3 months" }) },
                    { value: "3-6 months", label: t("agent.flows.newCase.timelineOpt.threeToSix", { defaultValue: "3–6 months" }) },
                    { value: "Just exploring", label: t("agent.flows.newCase.timelineOpt.exploring", { defaultValue: "Just exploring" }) },
                  ]}
                />
              </Field>
            </div>
            <AIHint>
              {t("agent.flows.newCase.clientAI", {
                defaultValue:
                  "On creation we'll parse the brief into preferences, seed a shortlist from current listings, and flag your other cases that might match.",
              })}
            </AIHint>
          </>
        )}

        {step === 2 && type && <ReviewSummary type={type} form={form} />}

        {errMsg && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              borderRadius: 8,
              background: "rgba(191,56,56,0.08)",
              border: "1px solid rgba(191,56,56,0.4)",
              color: "#bf3838",
              fontSize: 12.5,
              lineHeight: 1.4,
            }}
          >
            {t("agent.flows.newCase.createFailed", { defaultValue: "Couldn't open case" })}: {errMsg}
          </div>
        )}
      </ModalBody>

      <ModalFooter
        leftHint={
          step === 0 ? null : <span>← {t("agent.flows.escCancel", { defaultValue: "Press Esc to cancel" })}</span>
        }
      >
        {step > 0 && (
          <Btn ghost onClick={() => setStep((s) => s - 1)}>
            {t("agent.flows.back", { defaultValue: "Back" })}
          </Btn>
        )}
        {step < 2 && (
          <Btn primary disabled={!canNext} onClick={() => setStep((s) => s + 1)}>
            {t("agent.flows.continue", { defaultValue: "Continue" })}
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Btn>
        )}
        {step === 2 && (
          <Btn primary disabled={submitting} onClick={() => void handleCreate()}>
            {submitting
              ? t("agent.flows.opening", { defaultValue: "Opening…" })
              : t("agent.flows.openCase", { defaultValue: "Open case" })}
          </Btn>
        )}
      </ModalFooter>
    </ModalShell>
  );
}

function CaseTypeCard({
  active,
  onClick,
  title,
  subtitle,
  icon,
  examples,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  examples: string[];
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "20px 18px",
        textAlign: "left",
        background: active ? "var(--ag-accent-soft)" : "var(--ag-surface)",
        border: `1.5px solid ${active ? "var(--ag-accent)" : "var(--ag-rule)"}`,
        borderRadius: 12,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        fontFamily: "var(--ag-font-ui)",
        color: "var(--ag-ink)",
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: active ? "var(--ag-accent)" : "var(--ag-surface-2)",
          color: active ? "var(--ag-bg)" : "var(--ag-accent)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontFamily: "var(--ag-font-serif)", fontSize: 17, fontWeight: 500, letterSpacing: -0.2 }}>
          {title}
        </div>
        <div style={{ fontSize: 11.5, color: active ? "var(--ag-accent)" : "var(--ag-ink-3)", marginTop: 2 }}>
          {subtitle}
        </div>
      </div>
      <ul
        style={{
          margin: "4px 0 0",
          padding: 0,
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {examples.map((e) => (
          <li
            key={e}
            style={{ fontSize: 11.5, color: "var(--ag-ink-3)", display: "flex", gap: 7 }}
          >
            <span style={{ color: "var(--ag-accent)" }}>·</span>
            {e}
          </li>
        ))}
      </ul>
    </button>
  );
}

function ReviewSummary({ type, form }: { type: "property" | "client"; form: WizardForm }) {
  const { t } = useTranslation();
  const rows: [string, string][] =
    type === "property"
      ? [
          [t("agent.flows.newCase.askingPrice", { defaultValue: "Asking" }), form.price || "—"],
          [t("agent.flows.newCase.size", { defaultValue: "Size" }), form.size ? `${form.size} sqft` : "—"],
          [t("agent.flows.newCase.beds", { defaultValue: "Bedrooms" }), form.beds || "—"],
          [t("agent.flows.newCase.engagement", { defaultValue: "Engagement" }), form.engagement || "—"],
          [t("agent.flows.newCase.seller", { defaultValue: "Seller" }), form.seller || "—"],
        ]
      : [
          [t("agent.flows.newCase.phone", { defaultValue: "Phone" }), form.phone || "—"],
          [t("agent.flows.newCase.budget", { defaultValue: "Budget" }), form.budget || "—"],
          [t("agent.flows.newCase.stage", { defaultValue: "Stage" }), form.stage || "—"],
          [t("agent.flows.newCase.timeline", { defaultValue: "Timeline" }), form.timeline || "—"],
        ];

  return (
    <>
      <div
        style={{
          padding: 16,
          borderRadius: 12,
          background: "var(--ag-surface)",
          border: "1px solid var(--ag-rule)",
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "var(--ag-ink-3)",
            marginBottom: 8,
          }}
        >
          {type === "property"
            ? t("agent.flows.newCase.summaryPropertyKicker", { defaultValue: "Property case" })
            : t("agent.flows.newCase.summaryClientKicker", { defaultValue: "Client case" })}
        </div>
        <div
          style={{
            fontFamily: "var(--ag-font-serif)",
            fontSize: 19,
            fontWeight: 500,
            marginBottom: 12,
          }}
        >
          {type === "property"
            ? t("agent.caseName.sell", { name: form.address || "—" })
            : t("agent.caseName.help", { client: form.clientName || "—" })}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {rows.map(([k, v]) => (
            <div key={k}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "var(--ag-ink-3)",
                  marginBottom: 3,
                }}
              >
                {k}
              </div>
              <div style={{ fontSize: 13.5, color: "var(--ag-ink)" }}>{v}</div>
            </div>
          ))}
          {type === "client" && form.brief && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "var(--ag-ink-3)",
                  marginBottom: 4,
                }}
              >
                {t("agent.flows.newCase.briefShort", { defaultValue: "Brief" })}
              </div>
              <div
                style={{
                  fontFamily: "var(--ag-font-serif)",
                  fontSize: 14,
                  color: "var(--ag-ink-2)",
                  lineHeight: 1.5,
                }}
              >
                {form.brief}
              </div>
            </div>
          )}
        </div>
      </div>

      <AIHint>
        {type === "property"
          ? t("agent.flows.newCase.summaryPropertyAI", {
              defaultValue:
                "What happens when you open this case: comparables pulled, listing copy drafted, viewing calendar suggested, buyer-lead pipeline initialized.",
            })
          : t("agent.flows.newCase.summaryClientAI", {
              defaultValue:
                "What happens when you open this case: brief parsed into preferences, candidates surfaced from current listings, cross-checked against your other cases, first-week tasks added.",
            })}
      </AIHint>
    </>
  );
}

function parseMoney(s?: string): number | null {
  if (!s) return null;
  const cleaned = s.replace(/[^0-9.kKmM]/g, "");
  if (!cleaned) return null;
  const lower = cleaned.toLowerCase();
  if (lower.endsWith("m")) return Math.round(parseFloat(lower) * 1_000_000);
  if (lower.endsWith("k")) return Math.round(parseFloat(lower) * 1_000);
  const n = parseFloat(lower);
  if (Number.isNaN(n)) return null;
  return n < 1000 ? Math.round(n * 1_000_000) : Math.round(n);
}

function guessProjectName(address: string): string {
  // First chunk before "#" or "," — good-enough heuristic for a short_name.
  const before = address.split(/[#,]/)[0]?.trim();
  return before || address.slice(0, 40);
}

function mapStage(stage: string): CaseStage {
  switch (stage) {
    case "Briefing":
      return "New";
    case "Shortlisting":
      return "Active";
    case "Viewings":
      return "Viewing";
    case "Offer":
      return "Offer / Negotiation";
    default:
      return "New";
  }
}

// The case model has no dedicated timeline column — urgency is the stored
// "how soon" signal (and what the buyer report + dashboard read). Fold the
// wizard's 4 timeline options into the 3 urgency buckets. The reverse map
// in the case header (agent.case.timelineByUrgency) mirrors this so the
// agent sees back a horizon consistent with what they picked.
function mapTimelineToUrgency(timeline?: string): CaseUrgency {
  switch (timeline) {
    case "This month":
      return "high";
    case "1-3 months":
      return "medium";
    case "3-6 months":
    case "Just exploring":
      return "low";
    default:
      return "medium";
  }
}
